import { initial } from "@/utilities/functions/initial";
import { last } from "@/utilities/functions/last";
import { get as getPath } from "@/utilities/functions/get";
import { set as setPath } from "@/utilities/functions/set";
import { omit } from "@/utilities/functions/omit";
import { isNotNil } from "@/utilities/functions/isNotNil";
import { isNil } from "@/utilities/functions/isNil";

/**
 * Retrieves a path from localStorage
 * @param {string[] | string} keys The keys of the path
 */
export const get = <T>(keys: string[] | string) => getOr<T, null>(keys, null);

/**
 * Retrieves a path from localStorage or returns the fallback value
 */
export const getOr = <T, U>(keys: string[] | string, defaultTo: U): T | typeof defaultTo => {
  const path = ([] as string[]).concat(keys);
  const found = localStorage.getItem(path[0]);
  const parsed: T | null | { [key: string]: any } = found ? JSON.parse(found) : null;
  if (path.length === 1) {
    if (isNotNil(parsed)) return parsed as T;
  }
  if (isNil(parsed)) return defaultTo;
  const nextObj = parsed as { [key: string]: any };

  return getPath(nextObj, path.slice(1).join("."));
};

/**
 * Saves a nested value in localStorage.
 * The first key identifies the item in localstorage,
 * the others are used for the nested object.
 * Returns the outermost object.
 */
export const set = <T>(keys: string[] | string, value: T): typeof value => {
  const path = ([] as string[]).concat(keys);
  // If it's saving the record just save and returns
  if (path.length === 1) {
    localStorage.setItem(path[0], JSON.stringify(value));
    return value;
    // If it wants to do nesting set
  } else {
    // Get the outermost key
    const found = getOr<{ [key: string]: any }, object>(path[0], {});
    // Populate the inenr object
    const updated = setPath(found, path.slice(1), value);
    // Update the outermost key
    set(path[0], updated);
    return value;
  }
};

/**
 * Passed a key or a path gets a value from the localStorage.
 * If the value is not present is created and then returned.
 */
export const getOrSet = <T>(keys: string[] | string, value: T): typeof value => {
  const found = get<T>(keys);
  if (isNil(found)) return set(keys, value);
  return found;
};

/**
 * Passed a key or a path will remove the key and save the updated object
 */
export const removeKey = (keys: string | string[]) => {
  const path = ([] as string[]).concat(keys);
  if (path.length > 1) {
    // Provided the last key is the one to remove, we need the last but one
    const pathToObjectToEdit = initial(path);
    const exists = get<Record<string, any>>(pathToObjectToEdit);
    if (isNotNil(exists)) {
      const updated = omit([last(path)])(exists);
      return set(pathToObjectToEdit, updated);
    }
  } else {
    localStorage.removeItem(path[0]);
  }
};
